import { isDefined } from "@clipboard-health/util-ts";
import { patch } from "@src/appV2/api/api";
import { API_TYPES } from "@src/appV2/api/constants";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

import { invalidatePlacementListCaches } from "./placementListCacheUtils";

export const INTERVIEW_EXPERIENCE_RESULT = {
  didNotInterview: "did-not-interview",
  notExpectedButInterviewed: "not-expected-but-interviewed",
  expectedButWaited: "expected-but-waited",
  expectedAndSmooth: "expected-and-smooth",
} as const;
export type InterviewExperienceResult =
  (typeof INTERVIEW_EXPERIENCE_RESULT)[keyof typeof INTERVIEW_EXPERIENCE_RESULT];

export const WORKER_OUTCOME_STEP = {
  interviewExperience: "interview-experience",
  interviewOutcome: "interview-outcome",
} as const;

export const WORKER_OUTCOME_RESULT = {
  gotOffer: "got-offer",
  gotOfferKnowNextSteps: "got-offer-know-next-steps",
  gotOfferFollowUpNextSteps: "got-offer-follow-up-next-steps",
  gotOfferNoNextSteps: "got-offer-no-next-steps",
  notHeardBack: "not-heard-back",
  notHeardBackFewDays: "not-heard-back-few-days",
  notHeardBackWithinWeek: "not-heard-back-within-week",
  notHeardBackNotSpecified: "not-heard-back-not-specified",
  gotOfferFollowUpFewDays: "got-offer-follow-up-few-days",
  gotOfferFollowUpWithinWeek: "got-offer-follow-up-within-week",
  gotOfferFollowUpNotSpecified: "got-offer-follow-up-not-specified",
  scheduleMisalignment: "schedule-misalignment",
  payMisalignment: "pay-misalignment",
  notEnoughExperience: "not-enough-experience",
  notSure: "not-sure",
  notOfferTheRoleOther: "not-offer-the-role-other",
  interviewNotAvailable: "interview-not-available",
  interviewerUnavailableWorkerLeft: "interviewer-unavailable-worker-left",
  noLongerHiring: "no-longer-hiring",
  fillOutApplicationFirst: "fill-out-application-first",
  notInterviewOther: "not-interview-other",
} as const;
export type WorkerOutcomeResult =
  (typeof WORKER_OUTCOME_RESULT)[keyof typeof WORKER_OUTCOME_RESULT];

export interface WorkerOutcomeStep {
  step: (typeof WORKER_OUTCOME_STEP)[keyof typeof WORKER_OUTCOME_STEP];
  value: InterviewExperienceResult | WorkerOutcomeResult;
}

export function buildWorkerOutcomeSteps(
  experience: InterviewExperienceResult | undefined,
  finalResult: WorkerOutcomeResult
): WorkerOutcomeStep[] | undefined {
  if (!experience) {
    return undefined;
  }
  return [
    { step: WORKER_OUTCOME_STEP.interviewExperience, value: experience },
    { step: WORKER_OUTCOME_STEP.interviewOutcome, value: finalResult },
  ];
}

const updateInterviewBodySchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.interview),
    id: z.string(),
    attributes: z.object({
      status: z.string().optional(),
      workerOutcome: z
        .object({
          dismissedAt: z.date().optional(),
          result: z.nativeEnum(WORKER_OUTCOME_RESULT).optional(),
          message: z.string().optional(),
          steps: z
            .array(
              z.object({
                step: z.nativeEnum(WORKER_OUTCOME_STEP),
                value: z.string(),
              })
            )
            .optional(),
        })
        .optional(),
    }),
  }),
});

export type UpdateInterviewBody = z.infer<typeof updateInterviewBodySchema>;

interface UpdateInterviewParams {
  interviewId: string;
  attributes: UpdateInterviewBody["data"]["attributes"];
}

export function useUpdateInterview(
  options: UseMutationOptions<void, AxiosError, UpdateInterviewParams> = {}
) {
  const queryClient = useQueryClient();
  const worker = useDefinedWorker();

  return useMutation({
    mutationFn: async ({ interviewId, attributes }) => {
      await patch({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/interviews/${interviewId}`,
        data: {
          data: {
            type: API_TYPES.interview,
            id: interviewId,
            attributes,
          },
        },
        requestSchema: updateInterviewBodySchema,
        responseSchema: z.unknown(),
      });

      logEvent(APP_V2_APP_EVENTS.UPDATE_INTERVIEW_SUCCESS, {
        interviewId,
        attributes,
      });

      // Only status changes (cancel/reschedule) affect whether a placement appears in
      // the discovery list. Outcome/feedback updates don't, so skip the invalidation to
      // avoid redundant refetches during multi-step feedback flows. Fire-and-forget so
      // the mutation doesn't block on list refetches.
      if (isDefined(attributes.status)) {
        void invalidatePlacementListCaches(queryClient, worker.userId);
      }
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.UPDATE_INTERVIEW_FAILURE,
    },
    ...options,
  });
}
